/**
 * Build Your Own Gift Box — Option A cart (linked line items via _box_id).
 * Vanilla JS stepper: hamper → organizer → jewellery → freebies → review.
 */
(function () {
  const ROOT_SELECTOR = '[data-byob]';

  class BuildYourOwnBox {
    constructor(root) {
      this.root = root;
      this.config = this.parseConfig();
      this.state = {
        step: 1,
        maxReached: 1,
        hamperKey: null,
        organizer: null,
        jewellery: [],
        freebies: [],
        boxQty: 1,
        jewelleryLoaded: false,
      };
      this.cacheElements();
      this.bindEvents();
      this.setupFooterPinning();
      this.render();
      this.loadJewelleryCatalog();
    }

    parseConfig() {
      const el =
        this.root.querySelector('[data-byob-config]') ||
        this.root.parentElement?.querySelector(':scope > [data-byob-config]');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('BYOB: invalid config', e);
        return {};
      }
    }

    cacheElements() {
      this.els = {
        steppers: this.root.querySelectorAll('[data-byob-step]'),
        panels: this.root.querySelectorAll('[data-byob-panel]'),
        hamperCards: this.root.querySelectorAll('[data-byob-hamper]'),
        organizerCards: this.root.querySelectorAll('[data-byob-organizer]'),
        jewelleryItems: this.root.querySelectorAll('[data-byob-jewellery]'),
        jewelleryGrid: this.root.querySelector('[data-byob-jewellery-grid]'),
        jewelleryLimitMsg: this.root.querySelector('[data-byob-jewellery-limit-msg]'),
        jewelleryCount: this.root.querySelector('[data-byob-jewellery-count]'),
        jewelleryLimit: this.root.querySelector('[data-byob-jewellery-limit]'),
        gameCards: this.root.querySelectorAll('[data-byob-game-card]'),
        pills: this.root.querySelectorAll('[data-byob-pill]'),
        nextBtns: this.root.querySelectorAll('[data-byob-next]'),
        backBtns: this.root.querySelectorAll('[data-byob-back]'),
        footer: this.root.querySelector('[data-byob-footer]'),
        footerAnchor: this.root.querySelector('[data-byob-footer-anchor]'),
        footerStrip: this.root.querySelector('[data-byob-footer-strip]'),
        footerCount: this.root.querySelector('[data-byob-footer-count]'),
        footerTotal: this.root.querySelector('[data-byob-footer-total]'),
        footerBack: this.root.querySelector('[data-byob-footer-back]'),
        footerNext: this.root.querySelector('[data-byob-footer-next]'),
        review: this.root.querySelector('[data-byob-review]'),
        qtyInput: this.root.querySelector('[data-byob-box-qty]'),
        qtyMinus: this.root.querySelector('[data-byob-qty-minus]'),
        qtyPlus: this.root.querySelector('[data-byob-qty-plus]'),
        grandTotal: this.root.querySelector('[data-byob-grand-total]'),
        addToCart: this.root.querySelector('[data-byob-add-to-cart]'),
        status: this.root.querySelector('[data-byob-status]'),
        builder: this.root.querySelector('[data-byob-builder]'),
        steppersTrack: this.root.querySelector('[data-byob-steppers]'),
        stepperPrev: this.root.querySelector('[data-byob-stepper-prev]'),
        stepperNext: this.root.querySelector('[data-byob-stepper-next]'),
      };
    }

    bindEvents() {
      // Event delegation — more reliable than per-button binds (disabled/theme buttons)
      this.root.addEventListener('click', (e) => {
        const hamperBtn = e.target.closest('[data-byob-select-hamper]');
        const hamperCard = e.target.closest('[data-byob-hamper]');
        if (hamperBtn || (hamperCard && !e.target.closest('a'))) {
          const card = hamperBtn ? hamperBtn.closest('[data-byob-hamper]') : hamperCard;
          if (card) {
            e.preventDefault();
            if (!this.isCardAvailable(card)) {
              this.flashStatus(this.config.messages?.soldOut || 'This hamper is currently sold out.');
              return;
            }
            this.selectHamper(card.dataset.byobHamper, card);
            return;
          }
        }

        const organizerBtn = e.target.closest('[data-byob-select-organizer]');
        if (organizerBtn) {
          e.preventDefault();
          const card = organizerBtn.closest('[data-byob-organizer]');
          if (card) {
            if (!this.isCardAvailable(card)) {
              this.flashStatus(this.config.messages?.soldOut || 'This item is currently sold out.');
              return;
            }
            this.selectOrganizer(card);
          }
          return;
        }

        const jewelleryBtn = e.target.closest('[data-byob-toggle-jewellery]');
        if (jewelleryBtn) {
          e.preventDefault();
          const item = jewelleryBtn.closest('[data-byob-jewellery]');
          if (item) this.toggleJewellery(item);
          return;
        }

        const freebieBtn = e.target.closest('[data-byob-toggle-freebie]');
        if (freebieBtn) {
          e.preventDefault();
          const card = freebieBtn.closest('[data-byob-freebie], [data-byob-game-card]');
          if (card) {
            if (!this.isCardAvailable(card)) {
              this.flashStatus(this.config.messages?.soldOut || 'This item is currently sold out.');
              return;
            }
            this.toggleFreebie(card);
          }
          return;
        }

        // Footer nav — always derive target from current step (never stale data attributes)
        const footerNext = e.target.closest('[data-byob-footer-next]');
        if (footerNext) {
          e.preventDefault();
          this.handleFooterNext();
          return;
        }

        const footerBack = e.target.closest('[data-byob-footer-back]');
        if (footerBack) {
          e.preventDefault();
          this.handleFooterBack();
          return;
        }

        const nextBtn = e.target.closest('[data-byob-next]');
        if (nextBtn) {
          e.preventDefault();
          const target = Number(nextBtn.dataset.byobNext);
          if (this.canLeaveStep(this.state.step)) {
            this.goToStep(target);
          } else {
            this.flashStatus(this.config.messages?.incomplete || 'Complete this step to continue');
          }
          return;
        }

        const backBtn = e.target.closest('[data-byob-back]');
        if (backBtn) {
          e.preventDefault();
          this.goToStep(Number(backBtn.dataset.byobBack));
          return;
        }

        const pill = e.target.closest('[data-byob-pill]');
        if (pill) {
          e.preventDefault();
          this.filterJewellery(pill.dataset.byobPill);
          return;
        }

        const removeBtn = e.target.closest('[data-byob-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.removeFromBox(removeBtn.dataset.byobRemove, removeBtn.dataset.byobRemoveId);
          return;
        }

        const editBtn = e.target.closest('[data-byob-edit]');
        if (editBtn) {
          e.preventDefault();
          this.goToStep(Number(editBtn.dataset.byobEdit));
        }
      });

      this.root.addEventListener('keydown', (e) => {
        const card = e.target.closest('[data-byob-hamper]');
        if (card && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          if (!this.isCardAvailable(card)) {
            this.flashStatus(this.config.messages?.soldOut || 'This hamper is currently sold out.');
            return;
          }
          this.selectHamper(card.dataset.byobHamper, card);
        }
      });

      this.els.steppers.forEach((el) => {
        el.addEventListener('click', () => {
          const step = Number(el.dataset.byobStep);
          if (step <= this.state.maxReached) this.goToStep(step);
        });
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const step = Number(el.dataset.byobStep);
            if (step <= this.state.maxReached) this.goToStep(step);
          }
        });
      });

      this.els.qtyMinus?.addEventListener('click', () => this.setBoxQty(this.state.boxQty - 1));
      this.els.qtyPlus?.addEventListener('click', () => this.setBoxQty(this.state.boxQty + 1));
      this.els.qtyInput?.addEventListener('change', () => {
        this.setBoxQty(Number(this.els.qtyInput.value) || 1);
      });

      this.els.addToCart?.addEventListener('click', () => this.addToCart());

      this.els.stepperPrev?.addEventListener('click', () => this.scrollSteppers(-1));
      this.els.stepperNext?.addEventListener('click', () => this.scrollSteppers(1));
      this.els.steppersTrack?.addEventListener('scroll', () => this.updateStepperArrows(), { passive: true });
      window.addEventListener('resize', () => {
        this.updateStepperArrows();
        this.centerActiveStep();
      });
      // Initial arrow state after layout
      requestAnimationFrame(() => this.updateStepperArrows());
    }

    scrollSteppers(direction) {
      const track = this.els.steppersTrack;
      if (!track) return;
      const amount = Math.max(120, Math.floor(track.clientWidth * 0.7));
      track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    updateStepperArrows() {
      const track = this.els.steppersTrack;
      const prev = this.els.stepperPrev;
      const next = this.els.stepperNext;
      if (!track || !prev || !next) return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const overflow = track.scrollWidth > track.clientWidth + 4;

      if (!isMobile || !overflow) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }

      prev.hidden = false;
      next.hidden = false;
      const maxScroll = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= maxScroll - 2;
    }

    centerActiveStep() {
      const track = this.els.steppersTrack;
      if (!track) return;
      const active = track.querySelector('.byob__step.is-active');
      if (!active) return;

      const overflow = track.scrollWidth > track.clientWidth + 4;
      if (!overflow) {
        this.updateStepperArrows();
        return;
      }

      const steps = [...track.querySelectorAll('.byob__step')];
      const index = steps.indexOf(active);
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      let nextLeft = track.scrollLeft;

      if (index <= 0) {
        nextLeft = 0;
      } else if (index === steps.length - 1) {
        // Last step: pin to the end so the track doesn’t leave a hollow gap.
        nextLeft = maxScroll;
      } else {
        const trackRect = track.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const offset =
          activeRect.left - trackRect.left - (trackRect.width / 2 - activeRect.width / 2);
        nextLeft = track.scrollLeft + offset;
      }

      track.scrollTo({
        left: Math.max(0, Math.min(maxScroll, nextLeft)),
        behavior: 'smooth',
      });
      this.updateStepperArrows();
    }

    handleFooterNext() {
      const step = Number(this.state.step);
      const nextMap = { 1: 2, 2: 3, 3: 4, 4: 5 };
      const target = nextMap[step];
      if (!target) return;
      if (!this.canLeaveStep(step)) {
        this.flashStatus(this.config.messages?.incomplete || 'Complete this step to continue');
        return;
      }
      this.goToStep(target);
    }

    handleFooterBack() {
      const step = Number(this.state.step);
      const backMap = { 2: 1, 3: 2, 4: 3 };
      const target = backMap[step];
      if (target) this.goToStep(target);
    }

    isCardAvailable(card) {
      // Cards without the attribute (e.g. jewellery, already filtered server-side) are available by default.
      return card?.dataset?.available !== 'false';
    }

    getHamper() {
      return this.config.hampers?.[this.state.hamperKey] || null;
    }

    jewelleryLimit() {
      return this.getHamper()?.jewelleryLimit || 0;
    }

    tierPrice() {
      return Number(this.getHamper()?.price) || 0;
    }

    isFreebiesRequired() {
      return this.config.gameCardRequired === true || this.config.gameCardRequired === 'true';
    }

    canLeaveStep(step) {
      if (step === 1) return Boolean(this.state.hamperKey);
      if (step === 2) return Boolean(this.state.organizer);
      if (step === 3) return this.state.jewellery.length === this.jewelleryLimit();
      if (step === 4) {
        if (!this.isFreebiesRequired()) return true;
        return this.state.freebies.length > 0;
      }
      return true;
    }

    freebieFromCard(card) {
      if (!card?.dataset?.variantId) return null;
      return {
        id: String(card.dataset.variantId),
        productId: card.dataset.productId,
        handle: card.dataset.handle || '',
        title: card.dataset.title,
        sku: card.dataset.sku || '',
        image: card.dataset.image,
        price: 0,
        key: card.dataset.freebieKey || '',
        label: card.dataset.freebieLabel || card.dataset.title || 'Freebie',
        includes: card.querySelector('.byob__freebie-includes')?.textContent?.trim() || '',
      };
    }

    syncFreebiesFromDom() {
      const cards = this.els.gameCards || [];
      this.state.freebies = Array.from(cards)
        .filter((card) => this.isCardAvailable(card))
        .map((card) => this.freebieFromCard(card))
        .filter(Boolean);
    }

    toggleFreebie(card) {
      if (!this.isCardAvailable(card)) return;
      const item = this.freebieFromCard(card);
      if (!item) return;

      const index = this.state.freebies.findIndex((f) => String(f.id) === String(item.id));
      if (index > -1) {
        this.state.freebies.splice(index, 1);
      } else {
        this.state.freebies = [item];
      }

      this.renderGameCardSelection();
      this.renderFooter();
      this.updateNextButtons();
      if (this.state.step === 5) this.renderReview();
    }

    selectHamper(key, cardEl) {
      const keyStr = String(key || '');
      if (!keyStr) return;

      // Ensure config entry exists even if JSON was incomplete / product not assigned
      if (!this.config.hampers) this.config.hampers = {};
      if (!this.config.hampers[keyStr] && cardEl) {
        this.config.hampers[keyStr] = {
          title: cardEl.dataset.title || `Hamper ${keyStr}`,
          price: Number(cardEl.dataset.price) || 0,
          jewelleryLimit: Number(cardEl.dataset.jewelleryLimit) || 0,
          variantId: cardEl.dataset.variantId || null,
          handle: cardEl.dataset.handle || null,
          available: cardEl.dataset.available !== 'false',
          image: cardEl.dataset.image || null,
        };
      }

      if (!this.config.hampers[keyStr]) return;
      if (this.config.hampers[keyStr].available === false) {
        this.flashStatus(this.config.messages?.soldOut || 'This hamper is currently sold out.');
        return;
      }

      const prev = this.state.hamperKey;
      const hamperChanged = Boolean(prev && prev !== keyStr);

      this.state.hamperKey = keyStr;

      // Changing hamper clears all downstream selections so the box rebuilds cleanly
      if (hamperChanged) {
        this.state.organizer = null;
        this.state.jewellery = [];
        this.state.freebies = [];
        this.state.maxReached = 1;
        this.renderOrganizerSelection();
        this.renderJewellerySelection();
        this.renderGameCardSelection();
      }

      this.renderHamperSelection();
      this.renderFooter();
      this.updateNextButtons();

      // Advance immediately (reference UX) — Next still works too
      this.goToStep(2);
    }

    selectOrganizer(card) {
      if (!this.isCardAvailable(card)) return;
      this.state.organizer = {
        id: card.dataset.variantId,
        productId: card.dataset.productId,
        handle: card.dataset.handle || '',
        title: card.dataset.title,
        sku: card.dataset.sku || '',
        image: card.dataset.image,
        price: Number(card.dataset.price) || 0,
      };
      this.renderOrganizerSelection();
      this.renderFooter();
      this.updateNextButtons();
    }

    toggleJewellery(item) {
      if (item.classList.contains('is-disabled') && !item.classList.contains('is-selected')) {
        this.flashStatus(this.config.messages?.limitReached || 'Remove an item to add another');
        return;
      }
      const id = item.dataset.variantId;
      const index = this.state.jewellery.findIndex((j) => j.id === id);
      if (index > -1) {
        this.state.jewellery.splice(index, 1);
      } else {
        if (this.state.jewellery.length >= this.jewelleryLimit()) {
          this.flashStatus(this.config.messages?.limitReached || 'Remove an item to add another');
          return;
        }
        this.state.jewellery.push({
          id,
          productId: item.dataset.productId,
          handle: item.dataset.handle || '',
          title: item.dataset.title,
          sku: item.dataset.sku || '',
          image: item.dataset.image,
          price: Number(item.dataset.price) || 0,
        });
      }
      this.renderJewellerySelection();
      this.renderFooter();
      this.updateNextButtons();
    }

    formatItemLabel(item, { withSku = false } = {}) {
      const title = (item?.title || '').trim();
      const sku = (item?.sku || '').trim();
      if (withSku && sku) return `${title} (SKU: ${sku})`;
      return title;
    }

    boxComponents() {
      return [this.state.organizer, ...this.state.jewellery, ...this.state.freebies].filter(
        (item) => item?.id
      );
    }

    inventoryVariantsPayload() {
      const counts = new Map();
      this.boxComponents().forEach((item) => {
        const id = String(item.id);
        if (!/^\d+$/.test(id)) return;
        counts.set(id, (counts.get(id) || 0) + 1);
      });
      return [...counts.entries()].map(([id, perBox]) => `${id}:${perBox}`).join(',');
    }

    visibleSkus() {
      return this.boxComponents()
        .map((item) => (item?.sku || '').trim())
        .filter(Boolean);
    }

    async assertComponentsAvailable() {
      const hamper = this.getHamper();
      const hamperItem =
        hamper && hamper.handle && hamper.variantId
          ? { id: hamper.variantId, handle: hamper.handle, title: hamper.title || 'This hamper' }
          : null;
      const items = [...this.boxComponents(), hamperItem].filter((item) => item && item.handle && item.id);

      // Run checks in parallel (not sequential) so one slow request doesn't add to every
      // other request's wall-clock exposure to a flaky connection.
      const results = await Promise.all(items.map((item) => this.checkComponentAvailable(item)));
      const unavailable = results.find((result) => !result.ok);
      if (unavailable) throw new Error(unavailable.message);
    }

    async checkComponentAvailable(item, attempt = 0) {
      const isTransient = (response) => !response || response.status >= 500;

      let response;
      try {
        response = await fetch(`${this.cartRoot()}products/${encodeURIComponent(item.handle)}.js`);
      } catch (networkError) {
        response = null;
      }

      // Retry once on a transient failure (offline blip, 5xx) before failing closed —
      // a real "sold out" (404 / valid response) never retries and blocks immediately.
      if (isTransient(response) && attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return this.checkComponentAvailable(item, attempt + 1);
      }

      if (!response) {
        return {
          ok: false,
          message: 'Could not verify stock right now. Please check your connection and try again.',
        };
      }

      if (!response.ok) {
        return {
          ok: false,
          message: `${item.title || 'One of the selected items'} is no longer available. Please choose another.`,
        };
      }

      const product = await response.json().catch(() => null);
      if (!product) {
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          return this.checkComponentAvailable(item, attempt + 1);
        }
        return { ok: false, message: 'Could not verify stock right now. Please try again.' };
      }

      const variant = (product.variants || []).find((row) => String(row.id) === String(item.id));
      if (!variant || variant.available === false) {
        return {
          ok: false,
          message: `${item.title || 'One of the selected items'} is no longer in stock. Please choose another.`,
        };
      }

      return { ok: true };
    }

    removeFromBox(type, id) {
      if (type === 'organizer') {
        this.state.organizer = null;
        this.renderOrganizerSelection();
      } else if (type === 'jewellery') {
        this.state.jewellery = this.state.jewellery.filter((j) => j.id !== id);
        this.renderJewellerySelection();
      } else if (type === 'game' || type === 'freebie') {
        this.state.freebies = this.state.freebies.filter((f) => String(f.id) !== String(id));
        this.renderGameCardSelection();
      }
      this.renderFooter();
      this.updateNextButtons();
      if (this.state.step === 5) this.renderReview();
    }

    filterJewellery(handle) {
      const pills = [...this.els.pills];
      const fallback = pills.find((pill) => pill.dataset.byobPill && pill.dataset.byobPill !== 'all');
      const active = handle && handle !== 'all' ? handle : fallback?.dataset.byobPill || '';
      pills.forEach((pill) => {
        const on = pill.dataset.byobPill === active;
        pill.classList.toggle('is-active', on);
        pill.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.els.jewelleryItems.forEach((item) => {
        const handles = String(item.dataset.collections || item.dataset.collection || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
        item.hidden = Boolean(active) && !handles.includes(active);
      });
    }

    setBoxQty(qty) {
      const next = Math.max(1, Math.min(99, Math.floor(qty) || 1));
      this.state.boxQty = next;
      if (this.els.qtyInput) this.els.qtyInput.value = String(next);
      this.renderReviewTotals();
    }

    goToStep(step) {
      const max = 5;
      const target = Math.max(1, Math.min(max, step));
      if (target > this.state.maxReached + 1) return;
      if (target > this.state.step && !this.canLeaveStep(this.state.step)) {
        this.flashStatus(this.config.messages?.incomplete || 'Complete this step to continue');
        return;
      }

      this.state.step = target;
      this.state.maxReached = Math.max(this.state.maxReached, target);

      if (target === 3) this.ensureJewelleryVisible();
      if (target === 4 && this.config.autoSelectGameCard) this.syncFreebiesFromDom();
      if (target === 5) this.renderReview();

      this.render();
      this.scrollToBuilder();
    }

    ensureJewelleryVisible() {
      this.els.jewelleryGrid?.removeAttribute('data-byob-lazy');
      this.loadJewelleryCatalog();
    }

    async loadJewelleryCatalog() {
      if (this._jewelleryLoadPromise) return this._jewelleryLoadPromise;
      this._jewelleryLoadPromise = this.fetchAndRenderJewellery().catch((error) => {
        this._jewelleryLoadPromise = null;
        console.error('BYOB: jewellery catalog failed', error);
        if (this.els.jewelleryGrid) {
          this.els.jewelleryGrid.innerHTML =
            '<p class="byob__empty">Could not load jewellery. Refresh the page and try again.</p>';
        }
      });
      return this._jewelleryLoadPromise;
    }

    jewellerySources() {
      if (Array.isArray(this.config.jewellerySources) && this.config.jewellerySources.length) {
        return this.config.jewellerySources.filter((source) => source?.handle);
      }
      return [...this.els.pills]
        .map((pill) => ({
          handle: pill.dataset.byobPill,
          label: (pill.textContent || '').trim(),
        }))
        .filter((source) => source.handle && source.handle !== 'all');
    }

    async fetchAndRenderJewellery() {
      const sources = this.jewellerySources();
      if (!sources.length) {
        if (this.els.jewelleryGrid) {
          this.els.jewelleryGrid.innerHTML =
            '<p class="byob__empty">Assign a jewellery collection (or filter blocks) in the theme editor.</p>';
        }
        return;
      }

      const byId = new Map();
      for (const source of sources) {
        if (!source?.handle) continue;
        const products = await this.fetchCollectionProducts(source.handle);
        products.forEach((product) => {
          const variant = this.firstAvailableVariant(product);
          if (!variant) return;
          const existing = byId.get(String(product.id));
          if (existing) {
            existing.collections.add(source.handle);
            return;
          }
          byId.set(String(product.id), {
            collections: new Set([source.handle]),
            product,
            variant,
          });
        });
      }

      const items = [...byId.values()];
      if (!items.length) {
        if (this.els.jewelleryGrid) {
          this.els.jewelleryGrid.innerHTML =
            '<p class="byob__empty">No jewellery is in stock right now.</p>';
        }
        this.els.jewelleryItems = [];
        return;
      }

      this.els.jewelleryGrid.innerHTML = items.map((row) => this.jewelleryCardHtml(row)).join('');
      this.els.jewelleryItems = this.root.querySelectorAll('[data-byob-jewellery]');
      this.state.jewelleryLoaded = true;
      this.els.jewelleryItems.forEach((item) => {
        item.querySelectorAll('img[data-src]').forEach((img) => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
        });
      });
      const activePill =
        [...this.els.pills].find((pill) => pill.classList.contains('is-active')) || this.els.pills[0];
      this.filterJewellery(activePill?.dataset.byobPill || '');
      this.renderJewellerySelection();
    }

    async fetchCollectionProducts(handle) {
      const products = [];
      const limit = 250;
      for (let page = 1; page <= 20; page += 1) {
        const url = `${this.cartRoot()}collections/${encodeURIComponent(handle)}/products.json?limit=${limit}&page=${page}`;
        const response = await fetch(url);
        if (!response.ok) break;
        const data = await response.json().catch(() => ({}));
        const batch = Array.isArray(data.products) ? data.products : [];
        products.push(...batch);
        if (batch.length < limit) break;
      }
      return products;
    }

    firstAvailableVariant(product) {
      if (product?.available === false) return null;
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      return variants.find((variant) => variant && variant.available === true) || null;
    }

    productImage(product) {
      const featured = product?.featured_image;
      if (typeof featured === 'string' && featured) return featured;
      if (featured?.src) return featured.src;
      const image = product?.images?.[0];
      if (typeof image === 'string' && image) return image;
      return image?.src || '';
    }

    variantPriceMajor(variant) {
      const raw = variant?.price;
      if (typeof raw === 'string' && raw.includes('.')) return Number(raw) || 0;
      const n = Number(raw);
      if (!Number.isFinite(n)) return 0;
      return Number.isInteger(n) ? n / 100 : n;
    }

    sizedImage(url, width) {
      if (!url) return '';
      const joiner = url.includes('?') ? '&' : '?';
      return `${url}${joiner}width=${width}`;
    }

    jewelleryCardHtml({ collections, product, variant }) {
      const handles = [...collections].join(',');
      const title = this.escape(product.title || '');
      const sku = this.escape(variant.sku || '');
      const image = this.productImage(product);
      const price = this.variantPriceMajor(variant);
      const thumb = this.sizedImage(image, 40);
      const full = this.sizedImage(image, 600);
      const media = image
        ? `<img data-src="${this.escape(full)}" src="${this.escape(thumb)}" alt="${title}" width="600" height="600" loading="lazy" class="byob__lazy-img">`
        : '';
      return `<article class="byob__product-card" role="listitem" data-byob-jewellery data-collection="${this.escape(handles.split(',')[0] || '')}" data-collections="${this.escape(handles)}" data-variant-id="${this.escape(String(variant.id))}" data-product-id="${this.escape(String(product.id))}" data-handle="${this.escape(product.handle || '')}" data-title="${title}" data-sku="${sku}" data-price="${price}" data-image="${this.escape(this.sizedImage(image, 200))}">
        <button type="button" class="byob__select-hit" data-byob-toggle-jewellery aria-pressed="false">
          <span class="byob__product-media">
            ${media}
            <span class="byob__check" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
          </span>
          <span class="byob__product-body">
            <span class="byob__product-title">${title}</span>
            <span class="byob__product-price">${this.formatMoney(price)}</span>
          </span>
        </button>
      </article>`;
    }

    scrollToBuilder() {
      const target = this.els.builder;
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    render() {
      this.renderSteppers();
      this.renderPanels();
      this.renderHamperSelection();
      this.renderOrganizerSelection();
      this.renderJewellerySelection();
      this.renderGameCardSelection();
      this.renderFooter();
      this.updateNextButtons();
      if (this.state.step === 5) this.renderReview();
    }

    renderSteppers() {
      this.els.steppers.forEach((el) => {
        const step = Number(el.dataset.byobStep);
        const active = this.state.step === step;
        const complete = this.state.step === 5 ? step <= 4 : step < this.state.step;
        const reachable = step <= this.state.maxReached;

        el.classList.toggle('is-active', active);
        el.classList.toggle('is-complete', complete && !active);
        el.classList.toggle('is-reachable', reachable);
        el.setAttribute('aria-current', active ? 'step' : 'false');
        el.setAttribute('aria-disabled', reachable ? 'false' : 'true');
        el.tabIndex = reachable ? 0 : -1;

        const num = el.querySelector('[data-byob-step-num]');
        if (num) {
          if (complete && !active) {
            num.innerHTML =
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          } else {
            num.textContent = String(step);
          }
        }
      });

      requestAnimationFrame(() => this.centerActiveStep());
    }

    renderPanels() {
      this.els.panels.forEach((panel) => {
        const step = Number(panel.dataset.byobPanel);
        const show = step === this.state.step;
        panel.hidden = !show;
        panel.setAttribute('aria-hidden', show ? 'false' : 'true');
      });
    }

    renderHamperSelection() {
      this.els.hamperCards.forEach((card) => {
        const available = this.isCardAvailable(card);
        const selected = available && card.dataset.byobHamper === this.state.hamperKey;
        card.classList.toggle('is-selected', selected);
        const btn = card.querySelector('[data-byob-select-hamper]');
        if (btn) {
          if (!available) {
            btn.setAttribute('aria-pressed', 'false');
            btn.disabled = true;
            btn.textContent = 'Sold Out';
            return;
          }
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          btn.textContent = selected
            ? this.config.labels?.selected || 'Selected'
            : this.config.labels?.select || 'Select';
        }
      });
    }

    renderOrganizerSelection() {
      const selectedId = this.state.organizer?.id;
      this.els.organizerCards.forEach((card) => {
        const available = this.isCardAvailable(card);
        const selected = available && card.dataset.variantId === selectedId;
        card.classList.toggle('is-selected', selected);
        const btn = card.querySelector('[data-byob-select-organizer]');
        if (btn) {
          if (!available) {
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('aria-label', 'Sold out');
            btn.disabled = true;
            return;
          }
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          btn.setAttribute('aria-label', selected ? 'Selected organizer' : 'Select organizer');
        }
      });
    }

    renderJewellerySelection() {
      const limit = this.jewelleryLimit();
      const count = this.state.jewellery.length;
      const atLimit = count >= limit;

      if (this.els.jewelleryCount) this.els.jewelleryCount.textContent = String(count);
      if (this.els.jewelleryLimit) this.els.jewelleryLimit.textContent = String(limit);
      if (this.els.jewelleryLimitMsg) {
        this.els.jewelleryLimitMsg.hidden = !atLimit;
      }

      this.els.jewelleryItems.forEach((item) => {
        const selected = this.state.jewellery.some((j) => j.id === item.dataset.variantId);
        item.classList.toggle('is-selected', selected);
        item.classList.toggle('is-disabled', atLimit && !selected);
        const btn = item.querySelector('[data-byob-toggle-jewellery]');
        if (btn) {
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          btn.disabled = atLimit && !selected;
        }
      });
    }

    renderGameCardSelection() {
      const selectedIds = new Set(this.state.freebies.map((f) => String(f.id)));
      this.els.gameCards.forEach((card) => {
        const available = this.isCardAvailable(card);
        const selected = available && selectedIds.has(String(card.dataset.variantId));
        card.classList.toggle('is-selected', selected);
        card.classList.toggle('is-included', selected);
        const btn = card.querySelector('[data-byob-toggle-freebie]');
        const hint = card.querySelector('[data-byob-freebie-hint]');
        if (!available) {
          if (btn) {
            btn.setAttribute('aria-pressed', 'false');
            btn.setAttribute('aria-label', 'Sold out');
            btn.disabled = true;
          }
          if (hint) hint.textContent = 'Currently unavailable';
          return;
        }
        if (btn) {
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          btn.setAttribute('aria-label', selected ? 'Selected freebies' : 'Select freebies');
        }
        if (hint) {
          hint.textContent = selected ? 'Added · free with this hamper' : 'Tap to add · free with this hamper';
        }
      });
    }

    updateNextButtons() {
      // Panel next buttons (hidden) + sticky footer next
      this.root.querySelectorAll('[data-byob-panel] [data-byob-next]').forEach((btn) => {
        const panel = btn.closest('[data-byob-panel]');
        if (!panel) return;
        const panelStep = Number(panel.dataset.byobPanel);
        btn.disabled = !this.canLeaveStep(panelStep);
      });
      if (this.els.footerNext && this.state.step >= 1 && this.state.step <= 4) {
        this.els.footerNext.disabled = !this.canLeaveStep(this.state.step);
      }
    }

    formatMoney(amount) {
      const value = Number(amount) || 0;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.config.currency || 'INR',
          maximumFractionDigits: 0,
        }).format(value);
      } catch (e) {
        return `₹${value}`;
      }
    }

    escape(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    boxItems() {
      const items = [];
      if (this.state.organizer) {
        items.push({ ...this.state.organizer, type: 'organizer', label: 'Organizer' });
      }
      this.state.jewellery.forEach((j, i) => {
        items.push({ ...j, type: 'jewellery', label: `Jewellery ${i + 1}` });
      });
      this.state.freebies.forEach((f) => {
        items.push({ ...f, type: 'freebie', label: f.label || 'Freebie' });
      });
      return items;
    }

    setupFooterPinning() {
      const bar = this.els.footer;
      const anchor = this.els.footerAnchor;
      if (!bar || !anchor) return;

      const shopifySection = this.root.closest('.shopify-section') || this.root;
      let sentinel = document.querySelector('[data-byob-release-sentinel]');
      if (!sentinel) {
        sentinel = document.createElement('div');
        sentinel.setAttribute('data-byob-release-sentinel', '');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText =
          'height:0;width:100%;margin:0;padding:0;pointer-events:none;overflow:hidden;';
        shopifySection.insertAdjacentElement('afterend', sentinel);
      }
      this._releaseSentinel = sentinel;

      const siteFooter =
        document.querySelector('#shopify-section-group-footer-group') ||
        document.querySelector('.shopify-section-group-footer-group') ||
        sentinel;

      let rafId = 0;
      let cachedBarHeight = 0;
      let lastPushUp = -1;

      const viewportBottom = () => {
        // visualViewport stays correct on mobile as the URL bar shows/hides;
        // plain innerHeight jumps and makes the fixed bar flicker with scroll.
        const vv = window.visualViewport;
        if (vv) return Math.round(vv.height + vv.offsetTop);
        return window.innerHeight;
      };

      const measureBar = () => {
        if (bar.hidden) return;
        // Measure only while pinned so height includes safe-area padding.
        bar.classList.add('is-pinned');
        cachedBarHeight = bar.offsetHeight || 96;
        const next = `${cachedBarHeight}px`;
        if (anchor.style.height !== next) anchor.style.height = next;
      };

      const clearPin = () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
        bar.classList.remove('is-pinned');
        bar.style.transform = '';
        anchor.style.height = '';
        cachedBarHeight = 0;
        lastPushUp = -1;
      };

      const updatePin = () => {
        if (bar.hidden) {
          clearPin();
          return;
        }

        // Keep fixed the whole time — slide up flush with the site footer top
        // (avoids the white gap from section padding / docking to the anchor).
        if (!bar.classList.contains('is-pinned')) {
          bar.classList.add('is-pinned');
          measureBar();
        } else if (!cachedBarHeight) {
          measureBar();
        }

        const dockTop = Math.round(siteFooter.getBoundingClientRect().top);
        const pushUp = Math.max(0, viewportBottom() - dockTop);
        if (pushUp === lastPushUp) return;
        lastPushUp = pushUp;
        // Always keep a 3d transform so the compositor layer stays stable (no flicker).
        bar.style.transform = `translate3d(0, ${-pushUp}px, 0)`;
      };

      const schedulePinUpdate = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          updatePin();
        });
      };

      this._footerPinUpdate = () => {
        measureBar();
        lastPushUp = -1;
        schedulePinUpdate();
      };
      this._footerPinClear = clearPin;

      window.addEventListener('scroll', schedulePinUpdate, { passive: true });
      window.addEventListener(
        'resize',
        () => {
          measureBar();
          lastPushUp = -1;
          schedulePinUpdate();
        },
        { passive: true }
      );

      if (window.visualViewport) {
        // Only reposition — remasuring height here reflows the page mid-scroll on iOS.
        window.visualViewport.addEventListener('resize', schedulePinUpdate, {
          passive: true,
        });
        window.visualViewport.addEventListener('scroll', schedulePinUpdate, {
          passive: true,
        });
      }

      if ('IntersectionObserver' in window) {
        this._footerObserver = new IntersectionObserver(schedulePinUpdate, {
          root: null,
          threshold: [0, 0.01, 1],
        });
        this._footerObserver.observe(sentinel);
        this._footerObserver.observe(anchor);
        if (siteFooter !== sentinel) this._footerObserver.observe(siteFooter);
      }

      requestAnimationFrame(() => {
        measureBar();
        updatePin();
      });
    }

    renderFooter() {
      if (!this.els.footer) return;
      const show = this.state.step >= 1 && this.state.step <= 4 && this.state.hamperKey;
      this.els.footer.hidden = !show;
      if (!show) {
        if (this._footerPinClear) this._footerPinClear();
        else {
          this.els.footer.classList.remove('is-pinned');
          this.els.footer.style.transform = '';
          if (this.els.footerAnchor) this.els.footerAnchor.style.height = '';
        }
        return;
      }

      const items = this.boxItems();
      if (this.els.footerCount) {
        this.els.footerCount.textContent = String(items.length);
      }
      if (this.els.footerTotal) {
        this.els.footerTotal.textContent = this.formatMoney(this.tierPrice());
      }
      if (this.els.footerStrip) {
        this.els.footerStrip.innerHTML = items
          .map((item) => {
            return `
          <div class="byob__mini-card">
            <div class="byob__mini-thumb">
              ${item.image ? `<img src="${this.escape(item.image)}" alt="" width="56" height="56" loading="lazy">` : ''}
            </div>
            ${
              item.type === 'freebie'
                ? `<span class="byob__mini-free" aria-label="Free">Free</span>
                   <button type="button" class="byob__mini-remove" data-byob-remove="${item.type}" data-byob-remove-id="${this.escape(item.id)}" aria-label="Remove ${this.escape(item.title)}">×</button>`
                : `<button type="button" class="byob__mini-remove" data-byob-remove="${item.type}" data-byob-remove-id="${this.escape(item.id)}" aria-label="Remove ${this.escape(item.title)}">×</button>`
            }
          </div>`;
          })
          .join('');
      }

      const step = Number(this.state.step);
      const nextLabel =
        step === 4
          ? this.config.labels?.review || 'Review Box'
          : this.config.labels?.next || 'Next';

      if (this.els.footerBack) {
        this.els.footerBack.hidden = step <= 1;
      }

      if (this.els.footerNext) {
        this.els.footerNext.textContent = nextLabel;
        this.els.footerNext.disabled = !this.canLeaveStep(step);
        this.els.footerNext.setAttribute('aria-label', nextLabel);
      }

      if (this._footerPinUpdate) {
        requestAnimationFrame(this._footerPinUpdate);
      }
    }

    renderReview() {
      if (!this.els.review) return;
      const hamper = this.getHamper();
      if (!hamper) {
        this.els.review.innerHTML = '<p>Please select a gift hamper to continue.</p>';
        return;
      }

      const rows = [];
      rows.push(`
        <li class="byob__review-item">
          <div class="byob__review-thumb">${hamper.image ? `<img src="${this.escape(hamper.image)}" alt="" loading="lazy" width="72" height="72">` : ''}</div>
          <div class="byob__review-meta">
            <p class="byob__review-label">Gift hamper</p>
            <p class="byob__review-title">${this.escape(hamper.title)}</p>
            <p class="byob__review-price">${this.formatMoney(hamper.price)}</p>
          </div>
          <button type="button" class="byob__edit-link" data-byob-edit="1">Edit</button>
        </li>`);

      if (this.state.organizer) {
        const o = this.state.organizer;
        rows.push(`
        <li class="byob__review-item">
          <div class="byob__review-thumb">${o.image ? `<img src="${this.escape(o.image)}" alt="" loading="lazy" width="72" height="72">` : ''}</div>
          <div class="byob__review-meta">
            <p class="byob__review-label">Organizer</p>
            <p class="byob__review-title">${this.escape(o.title)}</p>
            <p class="byob__review-free">Free</p>
          </div>
          <button type="button" class="byob__edit-link" data-byob-edit="2">Edit</button>
        </li>`);
      }

      this.state.jewellery.forEach((j, i) => {
        rows.push(`
        <li class="byob__review-item">
          <div class="byob__review-thumb">${j.image ? `<img src="${this.escape(j.image)}" alt="" loading="lazy" width="72" height="72">` : ''}</div>
          <div class="byob__review-meta">
            <p class="byob__review-label">Jewellery ${i + 1}</p>
            <p class="byob__review-title">${this.escape(j.title)}</p>
          </div>
          <button type="button" class="byob__edit-link" data-byob-edit="3">Edit</button>
        </li>`);
      });

      if (this.state.freebies.length) {
        const f = this.state.freebies[0];
        rows.push(`
        <li class="byob__review-item">
          <div class="byob__review-thumb">${f.image ? `<img src="${this.escape(f.image)}" alt="" loading="lazy" width="72" height="72">` : ''}</div>
          <div class="byob__review-meta">
            <p class="byob__review-label">Freebies</p>
            <p class="byob__review-title">${this.escape(f.title)}</p>
            ${f.includes ? `<p class="byob__review-includes">${this.escape(f.includes)}</p>` : ''}
            <p class="byob__review-free">Free</p>
          </div>
        </li>`);
      }

      this.els.review.innerHTML = `<ul class="byob__review-list">${rows.join('')}</ul>`;
      this.renderReviewTotals();

      if (this.els.addToCart) {
        const freebiesOk = !this.isFreebiesRequired() || this.state.freebies.length > 0;
        this.els.addToCart.disabled = !(
          this.state.hamperKey &&
          this.state.organizer &&
          this.state.jewellery.length === this.jewelleryLimit() &&
          freebiesOk
        );
      }
    }

    renderReviewTotals() {
      const total = this.tierPrice() * this.state.boxQty;
      if (this.els.grandTotal) this.els.grandTotal.textContent = this.formatMoney(total);
    }

    flashStatus(message, isError = true) {
      if (!this.els.status) return;
      this.els.status.hidden = false;
      this.els.status.textContent = message;
      this.els.status.classList.toggle('is-error', isError);
      clearTimeout(this._statusTimer);
      this._statusTimer = setTimeout(() => {
        this.els.status.hidden = true;
      }, 4000);
    }

    uuid() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `box-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    cartRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    async addToCart() {
      const hamper = this.getHamper();
      if (!hamper) {
        this.flashStatus('Please select a gift hamper.');
        return;
      }
      if (!hamper.variantId) {
        this.flashStatus('Hamper product is not configured. Assign it in the theme editor.');
        return;
      }
      if (!this.state.organizer || this.state.jewellery.length !== this.jewelleryLimit()) {
        this.flashStatus('Please complete your selections first.');
        return;
      }
      if (this.isFreebiesRequired() && !this.state.freebies.length) {
        this.flashStatus('Please select your freebies to continue.');
        return;
      }

      const btn = this.els.addToCart;
      const original = btn?.textContent;
      if (btn) {
        btn.disabled = true;
        btn.textContent = this.config.labels?.adding || 'Adding…';
      }

      const qty = this.state.boxQty;

      try {
        await this.assertComponentsAvailable();
      } catch (stockError) {
        this.flashStatus(stockError.message || 'One of the selected pieces is out of stock.');
        if (btn) {
          btn.disabled = false;
          btn.textContent = original || 'Add Box to Cart';
        }
        return;
      }

      // Customer-facing properties: names only (review, cart, GoKwik).
      // SKUs go on underscore properties so they appear in Shopify Admin
      // on the order, not on the storefront.
      const properties = {
        Organizer: `${this.formatItemLabel(this.state.organizer)} (Free)`,
      };

      this.state.jewellery.forEach((j, i) => {
        properties[`Jewellery ${i + 1}`] = this.formatItemLabel(j);
        if (j.sku) properties[`_Jewellery ${i + 1} SKU`] = j.sku;
      });

      if (this.state.organizer?.sku) {
        properties['_Organizer SKU'] = this.state.organizer.sku;
      }

      const freebieSummary = (this.config.freebiesIncludes || '').trim();
      this.state.freebies.forEach((f) => {
        const title = this.formatItemLabel(f);
        const label = (f.label || 'Freebie').trim() || 'Freebie';
        const generic =
          !title ||
          title.toLowerCase() === label.toLowerCase() ||
          title.toLowerCase().startsWith('freebies');
        properties[label] = generic && freebieSummary ? `${freebieSummary} (Free)` : `${title} (Free)`;
        if (f.sku) properties[`_${label} SKU`] = f.sku;
      });

      const allSkus = this.visibleSkus();
      if (allSkus.length) properties._SKUs = allSkus.join(', ');

      properties._box_contents = [
        `Organizer: ${this.formatItemLabel(this.state.organizer, { withSku: true })} (Free)`,
        ...this.state.jewellery.map((j, i) => `Jewellery ${i + 1}: ${this.formatItemLabel(j, { withSku: true })}`),
        ...this.state.freebies.map((f) => `${f.label || 'Freebie'}: ${this.formatItemLabel(f, { withSku: true })} (Free)`),
      ]
        .filter(Boolean)
        .join(' · ');
      properties._hamper = hamper.title || '';
      properties._box_id = this.uuid();
      properties._box_type = 'gift_hamper';
      properties._inventory_variants = this.inventoryVariantsPayload();

      const items = [
        {
          id: Number(hamper.variantId),
          quantity: qty,
          properties,
        },
      ];

      try {
        const sections = this.getCartSections();
        const payload = { items };
        if (sections.length) {
          payload.sections = sections.join(',');
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(`${this.cartRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Could not add box to cart');
        }

        this.flashStatus(this.config.messages?.added || 'Your gift box has been added!', false);

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: this.root.id,
              data: {
                source: 'build-your-own-box',
                itemCount: qty,
                sections: data.sections,
              },
            },
          })
        );

        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else if (this.config.cartRedirect === 'cart') {
          window.location.href = `${this.cartRoot()}cart`;
        }
      } catch (error) {
        this.flashStatus(error.message || 'Something went wrong. Please try again.');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.textContent = original || 'Add Box to Cart';
        }
      }
    }

    getCartSections() {
      const ids = [];
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer?.dataset?.sectionId) ids.push(cartDrawer.dataset.sectionId);
      document.querySelectorAll('[data-section-id], .shopify-section').forEach((el) => {
        const id = el.dataset.sectionId || el.id?.replace(/^shopify-section-/, '');
        if (id && (id.includes('cart') || id.includes('header'))) ids.push(id);
      });
      return [...new Set(ids)].slice(0, 5);
    }
  }

  function init(scope) {
    const roots = (scope || document).querySelectorAll?.(ROOT_SELECTOR) || [];
    roots.forEach((root) => {
      if (root.dataset.byobReady) return;
      root.dataset.byobReady = 'true';
      new BuildYourOwnBox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root =
      event.target.querySelector?.(ROOT_SELECTOR) ||
      (event.target.matches?.(ROOT_SELECTOR) ? event.target : null);
    if (root) {
      delete root.dataset.byobReady;
      new BuildYourOwnBox(root);
    }
  });
})();
